import { datadogRum } from '@datadog/browser-rum'

const RELOAD_TIMESTAMP_KEY = 'sx-chunk-load-reload-at'
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000

// Known messages emitted by Vite/browsers when a lazy-loaded chunk fails to
// load (blocked CDN request, stale hash after a deploy, flaky network).
const CHUNK_LOAD_ERROR_PATTERNS = [
  /cross-origin script load denied/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
  /loading( css)? chunk .* failed/i,
]

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  const { name, message } = error as { name?: unknown; message?: unknown }

  if (name === 'ChunkLoadError') return true
  if (typeof message !== 'string') return false

  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

/**
 * Reloads the page once to recover from a failed chunk load (a fresh load
 * picks up the latest index.html, which fixes stale chunk hashes after a
 * deploy). Returns false when a reload was already attempted recently, so
 * callers can fall back to an error page instead of reload-looping.
 */
export function attemptChunkLoadAutoReload(error: unknown, source: string): boolean {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY)) || 0
    if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return false

    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()))
  } catch {
    // Without sessionStorage we cannot guard against a reload loop, so let
    // the error surface to the error boundary instead.
    return false
  }

  datadogRum.addAction('chunk_load_auto_reload', {
    source,
    errorMessage: error instanceof Error ? error.message : String(error),
  })

  window.location.reload()
  return true
}

export function registerChunkPreloadErrorHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const error = (event as Event & { payload?: Error }).payload

    if (attemptChunkLoadAutoReload(error, 'vite-preload')) {
      // The reload recovers this failure; stop Vite from also throwing it.
      event.preventDefault()
    }
  })
}
